import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { ShieldCheck, MessageSquareText } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Alert,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@dosa/ui";
import { requestOtpSchema, verifyOtpSchema, type RequestOtpInput, type VerifyOtpInput } from "@dosa/validation";
import { useAuth } from "../../hooks/useAuth";

const roleHome: Record<string, string> = { PATIENT: "/patient", PHARMACIST: "/pharmacist", ADMIN: "/admin" };

/**
 * Two-step OTP login via Authentica (see apps/worker/src/auth/authentica.ts): request a code
 * to a phone/email, then verify it. The demo role switcher below is local-dev-only — see
 * useAuth.tsx's `loginAsDemoRole` for the build-time gate that keeps it out of production.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { user, requestOtp, verifyOtp, loginAsDemoRole } = useAuth();
  const [step, setStep] = useState<"contact" | "code">("contact");
  const [sentTo, setSentTo] = useState<{ phone?: string; email?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [pendingRole, setPendingRole] = useState<string | null>(null);

  const contactForm = useForm<RequestOtpInput>({
    resolver: zodResolver(requestOtpSchema),
    defaultValues: { method: "sms" },
  });
  const codeForm = useForm<VerifyOtpInput>({ resolver: zodResolver(verifyOtpSchema) });

  if (user) return <Navigate to={roleHome[user.role] ?? "/"} replace />;

  const onRequestOtp = async (values: RequestOtpInput) => {
    setError(null);
    try {
      await requestOtp(values);
      setSentTo({ phone: values.phone, email: values.email });
      codeForm.reset({ phone: values.phone, email: values.email, otp: "" });
      setStep("code");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send verification code");
    }
  };

  const onVerifyOtp = async (values: VerifyOtpInput) => {
    setError(null);
    try {
      await verifyOtp({ ...values, phone: sentTo.phone, email: sentTo.email });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid or expired code");
    }
  };

  const handleDemoLogin = async (role: "PATIENT" | "PHARMACIST" | "ADMIN") => {
    setPendingRole(role);
    await loginAsDemoRole(role);
    setPendingRole(null);
  };

  const method = contactForm.watch("method");

  return (
    <div className="container-app flex min-h-[70vh] items-center justify-center py-16">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="mb-2 inline-flex w-fit rounded-md bg-primary-50 p-2.5 text-primary-700">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <CardTitle>{t("auth.loginTitle")}</CardTitle>
          <CardDescription>{t("auth.loginSubtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && <Alert variant="error">{error}</Alert>}

          {step === "contact" ? (
            <form onSubmit={contactForm.handleSubmit(onRequestOtp)} className="space-y-4" noValidate>
              <div>
                <FieldLabel htmlFor="method">{t("auth.otpMethod")}</FieldLabel>
                <Controller
                  control={contactForm.control}
                  name="method"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      items={{ sms: t("auth.otpMethodSms"), whatsapp: t("auth.otpMethodWhatsapp"), email: t("auth.otpMethodEmail") }}
                    >
                      <SelectTrigger id="method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sms">{t("auth.otpMethodSms")}</SelectItem>
                        <SelectItem value="whatsapp">{t("auth.otpMethodWhatsapp")}</SelectItem>
                        <SelectItem value="email">{t("auth.otpMethodEmail")}</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              {method === "email" ? (
                <div>
                  <FieldLabel htmlFor="email">{t("contact.email")}</FieldLabel>
                  <Input id="email" type="email" placeholder="you@example.com" {...contactForm.register("email")} />
                  <FieldError>{contactForm.formState.errors.email?.message}</FieldError>
                </div>
              ) : (
                <div>
                  <FieldLabel htmlFor="phone">{t("auth.phoneNumber")}</FieldLabel>
                  <Input id="phone" type="tel" placeholder="+9665XXXXXXXX" {...contactForm.register("phone")} />
                  <FieldError>{contactForm.formState.errors.phone?.message}</FieldError>
                </div>
              )}
              <Button type="submit" size="lg" className="w-full" isLoading={contactForm.formState.isSubmitting}>
                <MessageSquareText className="h-4 w-4" />
                {t("auth.sendCode")}
              </Button>
            </form>
          ) : (
            <form onSubmit={codeForm.handleSubmit(onVerifyOtp)} className="space-y-4" noValidate>
              <Alert variant="info">{t("auth.codeSentTo", { contact: sentTo.phone ?? sentTo.email ?? "" })}</Alert>
              <div>
                <FieldLabel htmlFor="otp">{t("auth.verificationCode")}</FieldLabel>
                <Input id="otp" inputMode="numeric" autoComplete="one-time-code" placeholder="123456" {...codeForm.register("otp")} />
                <FieldError>{codeForm.formState.errors.otp?.message}</FieldError>
              </div>
              <Button type="submit" size="lg" className="w-full" isLoading={codeForm.formState.isSubmitting}>
                {t("auth.verifyAndContinue")}
              </Button>
              <Button type="button" variant="ghost" size="sm" className="w-full" onClick={() => setStep("contact")}>
                {t("auth.changeContact")}
              </Button>
            </form>
          )}

          {import.meta.env.DEV && (
            <div className="border-t border-charcoal-100 pt-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-charcoal-400">
                {t("auth.roleSwitcherLabel")}
              </p>
              <div className="grid gap-2">
                <Button variant="outline" isLoading={pendingRole === "PATIENT"} onClick={() => handleDemoLogin("PATIENT")}>
                  {t("roles.patient")}
                </Button>
                <Button variant="outline" isLoading={pendingRole === "PHARMACIST"} onClick={() => handleDemoLogin("PHARMACIST")}>
                  {t("roles.pharmacist")}
                </Button>
                <Button variant="outline" isLoading={pendingRole === "ADMIN"} onClick={() => handleDemoLogin("ADMIN")}>
                  {t("roles.admin")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
